import React from "react";
import { useParams } from "react-router-dom";
import useAxios from "../../../../MainComponents/Hooks/useAxios";
import { Card, Row, Col } from "antd";
import {
  FaEnvelope,
  FaBook,
  FaCalendarAlt,
  FaCheckCircle,
  FaTasks,
  FaUser,
  FaPhone,
  FaPhoneAlt,
} from "react-icons/fa";
import { Loader } from "../../../../MainComponents";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronUp } from "@fortawesome/free-solid-svg-icons";

const ViewStudentCourseDetails = () => {
  const { studentID, courseID } = useParams();

  const [dataCourseDetails, errorCourseDetails, loadingCourseDetails] =
    useAxios(
      `${process.env.REACT_APP_STUDENT_COURSE_VIEW_DETAILS_API}${studentID}/${courseID}`,
      "GET",
      "GET",
      courseID
    );

  if (loadingCourseDetails) return <Loader />;
  if (errorCourseDetails) return <div>Error loading data</div>;

  const { student, course, sessions, course_expire } =
    dataCourseDetails?.data || {};

  return (
    <section className="playlist-wrapper flex justify-center mt-10">
      <div className="course-header-content relative transition-all duration-300 w-3/4 lg:w-full border-2 border-blue overflow-y-auto h-auto">
        {/* Header */}
        <div className="course-header bg-blue text-white p-4 shadow-lg w-full">
          <h2 className="course-title md:text-center text-xl font-bold">
            Course Content
          </h2>
          <p className="course-details md:text-center mt-3 ">
            {sessions?.length || 0} sessions • {course?.title}
          </p>
        </div>

        {/* Student + Course Info */}
        <Card bordered={false} className="shadow-lg m-4">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <div className="flex flex-col gap-2">
                <div className="flex items-center space-x-2">
                  <FaUser className="text-xl text-blue-500" />
                  <span className="font-semibold">Name:</span>
                  <span>{student?.name}</span>
                </div>

                <div className="flex items-center space-x-2">
                  <FaEnvelope className="text-xl text-green-500" />
                  <span className="font-semibold">Email:</span>
                  <span>{student?.email}</span>
                </div>

                <div className="flex items-center space-x-2">
                  <FaPhone className="text-xl text-purple-500" />
                  <span className="font-semibold">Mobile:</span>
                  <span>{student?.mobile}</span>
                </div>

                <div className="flex items-center space-x-2">
                  <FaPhoneAlt className="text-xl text-orange-500" />
                  <span className="font-semibold">Mother Mobile:</span>
                  <span>{student?.mother_mobile}</span>
                </div>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div className="flex items-center space-x-2">
                <FaBook className="text-xl text-green-500" />
                <span className="font-semibold">Course Title: </span>
                <span>{course?.title}</span>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Sessions */}
        <div className="playlist course-content w-full overflow-hidden bg-bg">
          {sessions && sessions.length > 0 ? (
            sessions.map((session, index) => (
              <div
                key={session.session_id}
                className="session-collapsed transition-all duration-700 ease-in-out w-full"
              >
                {/* Session Title */}
                <div
                  onClick={(e) => {
                    if (e.currentTarget.parentElement) {
                      e.currentTarget.parentElement.classList.toggle(
                        "collapse-session"
                      );
                    }
                  }}
                  className="session-title-wrapper w-full cursor-pointer border-y-2 px-4 flex justify-between items-center gap-5 h-16 bg-green"
                >
                  <div className="icon_title flex justify-start gap-5 items-center font-semibold text-lg pointer-events-none">
                    <FontAwesomeIcon
                      className="chevron-icon transition-all duration-300 ease-in-out"
                      icon={faChevronUp}
                    />
                    <p>{session.session_name}</p>
                  </div>
                </div>

                {/* Session Content */}
                <div className="session-content w-full transition-all duration-700 ease-in-out h-fit py-4 flex flex-col items-start px-6">
                  {/* Expiry Info */}
                  <Row gutter={[16, 16]} className="w-full">
                    <Col xs={24} md={12}>
                      <div className="flex items-center space-x-2">
                        <FaCalendarAlt className="text-xl text-purple-500" />
                        <span className="font-semibold">Expires At: </span>
                        <span>
                          {dayjs(session.expire_at).format("YYYY-MM-DD HH:mm")}
                        </span>
                      </div>
                    </Col>
                    <Col xs={24} md={12}>
                      <div className="flex items-center space-x-2">
                        <FaCalendarAlt className="text-xl text-gray-500" />
                        <span className="font-semibold">Remaining Days: </span>
                        <span>{session.remaining_days}</span>
                      </div>
                    </Col>
                  </Row>

                  {/* Quizzes */}
                  <Row gutter={[16, 16]} className="w-full mt-3">
                    <Col xs={24} md={12}>
                      <div className="flex items-center space-x-2">
                        <FaCheckCircle className="text-xl text-yellow-500" />
                        <span className="font-semibold">
                          Quiz Success Rate:
                        </span>
                        <span>
                          {session.quizzes?.[0]?.success_rate ?? "Not Assigned"}{" "}
                          {session.quizzes?.[0]?.success_rate && "%"}
                        </span>
                      </div>
                    </Col>
                    <Col xs={24} md={12}>
                      <div className="flex items-center space-x-2">
                        <FaCheckCircle className="text-xl text-orange-500" />
                        <span className="font-semibold">Quiz Result: </span>
                        <span>
                          {session.quizzes?.[0]?.result
                            ? `${session.quizzes[0].result.degree} / ${session.quizzes[0].result.out_of}`
                            : "No result"}
                        </span>
                      </div>
                    </Col>
                  </Row>

                  {/* Homeworks */}
                  <div className="w-full mt-3">
                    <div className="flex items-center space-x-2 mb-2">
                      <FaTasks className="text-xl text-teal-500" />
                      <span className="font-semibold">Homework:</span>
                    </div>
                    {session.homeworks && session.homeworks.length > 0 ? (
                      <ul className="list-disc ml-6">
                        {session.homeworks.map((hw) => (
                          <li key={hw.homework_id}>
                            <div className="flex flex-col">
                              <span className="font-semibold">
                                Title: {hw.title}
                              </span>
                              <span>
                                Success Rate: {hw.success_rate ?? "No result"}
                                {hw.success_rate && "%"}
                              </span>
                              <span>
                                Result:{" "}
                                {hw.result
                                  ? `${hw.result.degree} / ${hw.result.out_of}`
                                  : "No result"}
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="ml-6 text-gray-500">
                        No homeworks available
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-4">No sessions available</div>
          )}
        </div>

        {/* Course Expiry */}
        <Card bordered={false} className="shadow-lg m-4">
          <div className="flex items-center space-x-2">
            <FaCalendarAlt className="text-xl text-red-500" />
            <span className="font-semibold">Course Expiry: </span>
            <span>{dayjs(course_expire).format("YYYY-MM-DD HH:mm")}</span>
          </div>
        </Card>
      </div>
    </section>
  );
};

export default ViewStudentCourseDetails;
